import {getDatadogRum} from '../../../../util/datadog/datadogRum';
import * as Run from '../../../../util/runs';
import {fromJson} from '../../../../util/runs';
import {detectZeroLengthHistories} from '../../alerts';
import {type BucketedLine} from '../../types';
import {
  type BackendLinesDeltaQueryQuery,
  type GroupedBackendLinesDeltaQueryQuery,
} from './../../../../generated/graphql';
import {type QueryVars} from './queryVars';
import {
  type BucketedQueryRunsDeltas,
  type GroupedBucketedQueryRunWithGroupedRunConnection,
  type WithBaselinePinnedFlags,
} from './types';
import {profileRenderTimeSaved} from './utils';

const DD_RUM = getDatadogRum();

export function applyBackendLinesDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<BackendLinesDeltaQueryQuery['project']>['runs']
    >['deltas']
  >['delta'];
}): Record<string, BucketedLine> {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return currentRunsById;
  } else {
    // new diffOps or deleteOps means we need to refresh the reference for `runsById`
    currentRunsById = Object.assign({}, currentRunsById);
    deleteOps.forEach(rId => {
      delete currentRunsById[rId];
    });
  }

  delta.forEach(runDelta => {
    if (!runDelta.run) {
      console.error('Bucketed Delta Op returned without valid run');
      return;
    }

    try {
      const parsedRun = Run.fromJson(runDelta.run);
      if (parsedRun == null) {
        console.error('Unable to parse run from server in outliers reducer');
        return;
      }

      // Update the entire record for any diffOp
      // isBaseline/isPinned are added client-side by mergeBaselineAndPinnedRuns
      const runWithAddlFlags = runDelta.run as WithBaselinePinnedFlags<
        typeof runDelta.run
      >;

      currentRunsById[runDelta.run.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: runDelta.run.lines,
        isBaseline: runWithAddlFlags.isBaseline,
        isPinned: runWithAddlFlags.isPinned,
      };
    } catch (e) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Error parsing run data when applying delta',
        op: runDelta.op,
        runId: runDelta.run?.id,
        error: e instanceof Error ? e.message : 'Unknown error',
      });
    }
  });

  profileRenderTimeSaved(currentRunsById, 'backend');
  return currentRunsById;
}

export function applyGroupedBackendLinesDelta({
  currentRunsById,
  delta,
  order,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: NonNullable<
    NonNullable<
      NonNullable<GroupedBackendLinesDeltaQueryQuery['project']>['groupedRuns']
    >['deltas']
  >['delta'];
}): Record<string, BucketedLine> {
  const deleteOps = Object.keys(currentRunsById).filter(
    rId => !order.includes(rId)
  );
  if (delta.length === 0 && deleteOps.length === 0) {
    return currentRunsById;
  } else {
    // new diffOps or deleteOps means we need to refresh the reference for `runsById`
    currentRunsById = Object.assign({}, currentRunsById);
    deleteOps.forEach(rId => {
      delete currentRunsById[rId];
    });
  }

  delta.forEach(runDelta => {
    if (runDelta.__typename !== 'GroupBucketDiff') {
      // This should never happen because we check the typename before calling this function,
      // but it's required for type safety
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op called on non-grouped run',
        op: runDelta.op,
        runId: runDelta.run?.id,
      });
      return;
    }
    if (!runDelta.groupedRun) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Grouped Bucketed Delta Op returned without valid run',
        op: runDelta.op,
      });
      return;
    }
    // RunFromGraphQL doesn't allow null for createdAt
    const groupedRun = {
      ...runDelta.groupedRun,
      createdAt: runDelta.groupedRun.createdAt ?? undefined,
    };

    try {
      const parsedRun = Run.fromJson(groupedRun);
      if (parsedRun == null) {
        DD_RUM.addError(new Error('backend lines error'), {
          reason: 'Unable to parse run from server in outliers reducer',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
          projectId: groupedRun.projectId,
          username: groupedRun.user?.username,
        });
        return;
      }

      // Update the entire record for any diffOp
      currentRunsById[groupedRun.id] = {
        ...parsedRun,
        history: [], // when we use lines zero out the history
        historyLines: groupedRun.lines,
      };
    } catch (e) {
      DD_RUM.addError(new Error('backend lines error'), {
        reason: 'Error applying grouped backend lines delta',
        error: e instanceof Error ? e.message : 'Unknown error',
        groupedRunId: groupedRun.id,
        displayName: groupedRun.displayName,
        projectId: groupedRun.projectId,
        username: groupedRun.user?.username,
      });
    }
  });
  profileRenderTimeSaved(currentRunsById, 'grouped');
  return currentRunsById;
}

export function applyDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: BucketedQueryRunsDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}): Record<string, BucketedLine> {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      const parsedRun = fromJson(runDelta.run);
      if (parsedRun == null || runDelta.run == null) {
        DD_RUM.addError(new Error('backend lines error'), {
          reason: 'Unable to apply delta',
          runId: runDelta.run?.id,
          displayName: runDelta.run?.displayName,
          projectId: runDelta.run?.projectId,
          username: runDelta.run?.user?.username,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.run?.bucketedHistory ?? [],
        id: runDelta.run.id,
        displayName: runDelta.run.displayName ?? '',
        specs,
      });

      acc[runDelta.run.id] = {
        ...parsedRun,
        history: runDelta.run?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}

// Narrowed deltas type specifically for the GroupedRunConnection branch so array elements
// are not inferred as any when reducing over union-shaped arrays
export type GroupedBucketedRunDeltas = NonNullable<
  NonNullable<GroupedBucketedQueryRunWithGroupedRunConnection['deltas']>
>['delta'];

export function applyGroupedDelta({
  currentRunsById,
  delta,
  order,
  specs,
}: {
  currentRunsById: Record<string, BucketedLine>;
  order: string[];
  delta: GroupedBucketedRunDeltas;
  specs: QueryVars['bucketedHistorySpecs'];
}) {
  const newRunsById = delta.reduce(
    (acc, runDelta) => {
      if (runDelta.__typename !== 'GroupBucketDiff') {
        console.error('Grouped Bucketed Delta Op returned without valid run');
        return acc;
      }
      if (!runDelta.groupedRun) {
        console.error('Grouped Bucketed Delta Op returned without valid run');
        return acc;
      }
      // RunFromGraphQL doesn't allow null for createdAt
      const groupedRun = {
        ...runDelta.groupedRun,
        createdAt: runDelta.groupedRun.createdAt ?? undefined,
      };

      const parsedRun = fromJson(groupedRun);
      if (parsedRun == null) {
        DD_RUM.addError(new Error('backend lines error'), {
          reason: 'Unable to apply grouped delta',
          groupedRunId: groupedRun.id, // Note: This is only the ID of one of the runs in the group
          displayName: groupedRun.displayName,
          projectId: groupedRun.projectId,
          username: groupedRun.user?.username,
        });
        return acc;
      }

      detectZeroLengthHistories({
        histories: runDelta.groupedRun?.bucketedHistory ?? [],
        id: runDelta.groupedRun.id,
        displayName: runDelta.groupedRun.displayName ?? '',
        specs,
      });
      acc[runDelta.groupedRun.id] = {
        ...parsedRun,
        history: runDelta.groupedRun?.bucketedHistory ?? [],
      };
      return acc;
    },
    {} as Record<string, BucketedLine>
  );

  /**
   * TODO: THIS IS A HACK - REFACTOR
   * The problem is that we need to display the runs in the plot in the `order` that's returned from the delta. If you construct a JS object by adding the key/value pairs in a specific order, you can get a reliable iteration of those k/v pairs with Object.keys(). However this is an implicit behavior, and easily regressed. Since we return the order object _specifically_ to control this UI concern (since we can't reliably recreate the sort order determined by the server on the client as we might not have the proper info in the run to do the comparison) we need to find a way pipe this through so we can defer to it in the UI to display the runs.
   */
  const runsById: Record<string, BucketedLine> = {};
  order.forEach(rId => {
    runsById[rId] = newRunsById[rId] ?? currentRunsById[rId];
  });

  return runsById;
}
